import { CommonModule } from '@angular/common';
import { Component, effect, input, output, QueryList, signal, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { ButtonComponent } from "src/app/@core/components/ui/button/button.component";
import { DeleteConfirmationComponent } from "src/app/@core/components/ui/delete-confirmation/delete-confirmation.component";
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { ToastService } from 'src/app/@core/services/toast.service';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { SearchBarComponent } from "src/app/@shared/components/search-bar/search-bar.component";
import { IAgentMemory } from '../../../models/types';
import { GeneralAgentService } from '../../../services/general-agent.service';

@Component({
    selector: 'app-manage-memories',
    standalone: true,
    imports: [CommonModule, DialogComponent, FormsModule, MenuComponent, SearchBarComponent, ButtonComponent, DeleteConfirmationComponent, InfiniteScrollDirective],
    templateUrl: './manage-memories.component.html',
    styleUrl: './manage-memories.component.scss'
})
export class ManageMemoriesComponent {
    @ViewChildren(MenuComponent) menuComponents!: QueryList<MenuComponent>;

    visible = input<boolean>(false);
    visibleChange = output<boolean>();

    memories = signal<IAgentMemory[]>([]);
    nextToken = signal<string | null>(null);
    loading = signal<boolean>(false);

    searchQuery = signal<string>('');
    editingS3Key = signal<string | null>(null);
    editingLineIndex = signal<number | null>(null);
    editContent = signal<string>('');

    deleteConfirmationVisible = signal<boolean>(false);
    pendingDeleteS3Key = signal<string | null>(null);
    pendingDeleteLineIndex = signal<number | null>(null);
    isDeleteAll = signal<boolean>(false);

    /**
     * Checks if a memory line should be displayed
     * Filters out empty strings, whitespace-only strings, and markdown headers
     */
    private isValidMemoryLine(line: string): boolean {
        if (!line || typeof line !== 'string') return false;
        const trimmed = line.trim();
        // Filter out empty strings
        if (trimmed.length === 0) return false;
        // Filter out markdown headers (lines starting with #)
        if (trimmed.startsWith('#')) return false;
        return true;
    }

    get filteredMemories(): {
        content: string;
        s3Key: string;
        lineIndex: number;
        memory: IAgentMemory;
        threadId: string;
        threadMetadataId: string;
        createdAt: string | null;
    }[] {
        const allMemories = this.memories();
        if (!allMemories || allMemories.length === 0) return [];

        const query = this.searchQuery().toLowerCase().trim();
        const result: {
            content: string;
            s3Key: string;
            lineIndex: number;
            memory: IAgentMemory;
            threadId: string;
            threadMetadataId: string;
            createdAt: string | null;
        }[] = [];

        allMemories.forEach(memory => {
            if (memory.content && Array.isArray(memory.content)) {
                memory.content.forEach((item, lineIndex) => {
                    // Filter out invalid memory content (empty strings, markdown headers)
                    if (!this.isValidMemoryLine(item.text)) return;
                    // Apply search filter if query exists
                    if (!query || item.text.toLowerCase().includes(query)) {
                        result.push({
                            content: item.text,
                            s3Key: memory.key,
                            lineIndex,
                            memory,
                            threadId: item.threadId,
                            threadMetadataId: item.threadMetadataId,
                            createdAt: item.createdAt
                        });
                    }
                });
            }
        });

        return result;
    }

    constructor(
        private generalAgentService: GeneralAgentService,
        private router: Router,
        private toast: ToastService
    ) {
        effect(() => {
            if (this.visible()) {
                this.onDialogShow();
            }
        });
    }


    onDialogShow() {
        this.getMemories();
    }

    getMemories() {
        this.loading.set(true);
        this.generalAgentService.getMemories().subscribe((res) => {
            this.loading.set(false);
            if (res.data) {
                this.memories.set(res.data.memories || []);
                this.nextToken.set(res.data.nextToken || null);
            }
        });
    }

    loadMore() {
        const token = this.nextToken();
        if (!token || this.loading()) return;

        this.loading.set(true);
        this.generalAgentService.getMemories(token).subscribe((res) => {
            this.loading.set(false);
            if (res.data) {
                this.memories.update(current => [...current, ...(res.data.memories || [])]);
                this.nextToken.set(res.data.nextToken || null);
            }
        });
    }

    onSearchChange(value: string) {
        this.searchQuery.set(value);
    }

    openMenu(event: Event, index: number) {
        event.stopPropagation();
        const menuArray = this.menuComponents.toArray();
        if (menuArray[index]) {
            menuArray[index].toggle(event);
        }
    }

    getMenuItems(s3Key: string, lineIndex: number): CustomMenuItem[] {
        return [
            {
                label: 'Edit',
                icon: 'pi pi-pencil',
                callback: () => this.handleEdit(s3Key, lineIndex)
            },
            {
                label: 'Delete',
                icon: 'pi pi-trash',
                class: 'text-red-500',
                callback: () => this.handleDelete(s3Key, lineIndex)
            }
        ];
    }

    getFilterMenuItems(): CustomMenuItem[] {
        return [
            {
                label: 'Delete All Memories',
                icon: 'pi pi-trash',
                class: 'text-red-500',
                disabled: this.memories().length === 0,
                callback: () => {
                    this.isDeleteAll.set(true);
                    this.deleteConfirmationVisible.set(true);
                }
            }
        ];
    }

    handleDeleteConfirmation() {
        if (this.isDeleteAll()) {
            this.handleDeleteAll();
        } else {
            const s3Key = this.pendingDeleteS3Key();
            const lineIndex = this.pendingDeleteLineIndex();
            if (s3Key !== null && lineIndex !== null) {
                this.handleDeleteConfirm(s3Key, lineIndex);
            }
        }
        this.deleteConfirmationVisible.set(false);
        this.pendingDeleteS3Key.set(null);
        this.pendingDeleteLineIndex.set(null);
        this.isDeleteAll.set(false);
    }

    handleDeleteAll() {
        const allMemories = this.memories();
        if (!allMemories || allMemories.length === 0) return;

        this.generalAgentService.deleteAllMemories().subscribe((res) => {
            if (res.success) {
                this.memories.set([]);
                this.nextToken.set(null);
                this.toast.success('All memories deleted successfully');
            } else {
                this.toast.error('Failed to delete all memories');
            }
        });
    }

    handleEdit(s3Key: string, lineIndex: number) {
        const allMemories = this.memories();
        if (!allMemories || allMemories.length === 0) return;

        const memory = allMemories.find(m => m.key === s3Key);
        if (!memory || !memory.content || !memory.content[lineIndex]) return;

        this.editingS3Key.set(s3Key);
        this.editingLineIndex.set(lineIndex);
        this.editContent.set(memory.content[lineIndex].text);
    }

    handleSaveEdit(s3Key: string, lineIndex: number) {
        const updatedContent = this.editContent();

        // Send only the line change - backend preserves thread_ids
        this.generalAgentService.updateMemory(s3Key, lineIndex, updatedContent).subscribe((res) => {
            if (res.success) {
                this.toast.success('Memory updated successfully');
                this.getMemories();
                this.cancelEdit();
            } else {
                this.toast.error('Failed to update memory');
            }
        });
    }

    cancelEdit() {
        this.editingS3Key.set(null);
        this.editingLineIndex.set(null);
        this.editContent.set('');
    }

    handleDelete(s3Key: string, lineIndex: number) {
        this.pendingDeleteS3Key.set(s3Key);
        this.pendingDeleteLineIndex.set(lineIndex);
        this.isDeleteAll.set(false);
        this.deleteConfirmationVisible.set(true);
    }

    handleDeleteConfirm(s3Key: string, lineIndex: number) {
        // Delete single line - backend handles JSON update or file deletion if empty
        this.generalAgentService.deleteMemory(s3Key, lineIndex).subscribe((res) => {
            if (res.success) {
                this.toast.success('Memory deleted successfully');
                this.getMemories();
            } else {
                this.toast.error('Failed to delete memory');
            }
        });
    }

    openChat(item: { threadId: string; threadMetadataId: string }) {
        if (!item.threadId) return;

        // Navigate to the thread associated with this specific memory line
        this.router.navigate(['/bob/thread'], {
            queryParams: {
                threadId: item.threadId,
                threadMetadataId: item.threadMetadataId
            }
        });
        this.onDialogHide();
    }

    onDialogHide() {
        this.visibleChange.emit(false);
        this.searchQuery.set('');
        this.cancelEdit();
        this.nextToken.set(null);
    }
}
